import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { FeatureFlagsProvider, useFeatureFlags } from './contexts/FeatureFlagsContext';
import { useFeatureFlag } from './hooks/useFeatureFlag';

// Import pages
import LandingPage from './pages/LandingPage';
import LandingPageNew from './pages/LandingPageNew';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import RequestPasswordResetPage from './pages/RequestPasswordResetPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyEmailRequiredPage from './pages/VerifyEmailRequiredPage';
import DigestProfilesPage from './pages/DigestProfilesPage';
import PreferencesPage from './pages/PreferencesPage';
import ExplorePage from './pages/ExplorePage';
import ExplorePageV2 from './pages/ExplorePageV2';
import DigestsPage from './pages/DigestsPage';
import AdminPage from './pages/AdminPage';
import CopilotDashboardPage from './pages/CopilotDashboardPage';
// Phase A v2 pages
import DeeplinkPage from './pages/DeeplinkPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import TopicsDashboardPage from './pages/TopicsDashboardPage';
// Phase B: Community pages (rebranded to LitClub)
import CommunityPage from './pages/CommunityPage';
import LitClubPage from './pages/LitClubPage';
import SpecialtyForumPage from './pages/SpecialtyForumPage';
import ThreadDetailPage from './pages/ThreadDetailPage';
import NewThreadPage from './pages/NewThreadPage';
// v2.1: Verification and Inbox pages
import VerificationPage from './pages/VerificationPage';
import InboxPage from './pages/InboxPage';
// v3.0: PHI Guidelines page
import PhiGuidelinesPage from './pages/PhiGuidelinesPage';
// v3.0 Step 4: Plan page
import PlanPage from './pages/PlanPage';
// Phase 7: Audio Digests V2
import { AudioDigestsList, AudioDigestDetail } from './pages/AudioDigestsPage';
// Phase UX-E: Onboarding Preferences Wizard
import OnboardingPreferencesPage from './pages/OnboardingPreferencesPage';
// Membership comparison page
import MembershipPage from './pages/MembershipPage';
import PricingPage from './pages/PricingPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
// Navigation V2
import NavV2Gate from './pages/navv2/NavV2Gate';
import QueueDigestIndexPage from './pages/navv2/QueueDigestIndexPage';
import ProfileDetailShell from './pages/navv2/ProfileDetailShell';
import ArticleDetailShell from './pages/navv2/ArticleDetailShell';
import ScreenQueueBridge from './pages/navv2/ScreenQueueBridge';
import LitPortalPage from './pages/LitPortalPage';
// Beta Admin Dashboard
import BetaAdminPage from './pages/BetaAdminPage';
// LitScholar Deep Dive Page (TypeScript version)
import LitScholarPage from './pages/LitScholarPage';

// Workspace Shell V1 imports
import { WorkspaceShell } from './components/workspace';
import WorkspaceHomeRouter from './pages/workspace/WorkspaceHomeRouter';
import SearchWorkspacePage from './pages/workspace/SearchWorkspacePage';
import ScreenWorkspacePage from './pages/workspace/ScreenWorkspacePage';
import LearnWorkspacePage from './pages/workspace/LearnWorkspacePage';
import FeatureHubPage from './pages/FeatureHubPage';
import DigestProfilesWorkspacePage from './pages/workspace/DigestProfilesWorkspacePage';

/**
 * ConditionalCommunity — switches between V1 and V2/LitClub community page.
 * V2+ now uses LitClubPage with card-based design.
 * Waits for flags to load before rendering to prevent V1 from loading unnecessarily.
 * Fails closed: if flags unavailable, shows existing community (safe default).
 */
function ConditionalCommunity() {
  const { ready } = useFeatureFlags();
  const isV2 = useFeatureFlag('enable_community_v2');

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500" />
      </div>
    );
  }

  // V2 now uses the new LitClub page with card-based design
  return isV2 ? <LitClubPage /> : <CommunityPage />;
}

/**
 * ConditionalPreferences — switches between legacy and V2 Profiles manager.
 * Fails closed: if flags unavailable, shows existing PreferencesPage (safe default).
 */
function ConditionalPreferences() {
  const isProfilesEnabled = useFeatureFlag('enable_multi_digest_profiles');
  return isProfilesEnabled ? <DigestProfilesPage /> : <PreferencesPage />;
}

/**
 * ConditionalExplore — switches between simple, V2, and original Explore based on flags.
 * Priority: simple UI > V2 UI > original UI
 * Fails closed: if flags unavailable, shows existing Explore (safe default).
 */
function ConditionalExplore() {
  const isSimpleUI = useFeatureFlag('enable_explore_simple_pubmed_ui');
  const isV2 = useFeatureFlag('enable_explore_topic_search_v2');
  
  // Simple UI takes priority - minimal search without filters
  if (isSimpleUI) {
    return <ExplorePage />;  // ExplorePage now internally renders SimpleExploreUI when flag is true
  }
  
  // V2 with full filters and topic suggestions
  if (isV2) {
    return <ExplorePageV2 />;
  }
  
  // Original explore (fallback)
  return <ExplorePage />;
}

/**
 * ConditionalLanding — switches between old and new landing based on flag.
 * Fails closed: if flags are unavailable, shows the existing landing (safe default).
 */
function ConditionalLanding() {
  const isNewLanding = useFeatureFlag('enable_new_landing_page');
  return isNewLanding ? <LandingPageNew /> : <LandingPage />;
}

/**
 * ConditionalHome — legacy /home alias; LitHub is the app home.
 */
function ConditionalHome() {
  return <Navigate to="/library" replace />;
}

/**
 * WorkspaceLayout — Wraps workspace routes with the WorkspaceShell when feature flag is enabled.
 * Falls back to Layout for legacy routes.
 */
function WorkspaceLayout({ children }) {
  const isWorkspaceShell = useFeatureFlag('enable_workspace_shell_v1');
  
  if (isWorkspaceShell) {
    return <WorkspaceShell>{children}</WorkspaceShell>;
  }
  
  return <Layout>{children}</Layout>;
}

/**
 * ConditionalRedirect — Handles legacy route redirects when workspace shell is enabled.
 */
function WorkspaceRedirect({ legacyComponent: LegacyComponent, to }) {
  const { ready } = useFeatureFlags();
  const isWorkspaceShell = useFeatureFlag('enable_workspace_shell_v1');
  
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500" />
      </div>
    );
  }
  
  // If workspace shell is enabled, redirect to new route
  if (isWorkspaceShell) {
    return <Navigate to={to} replace />;
  }
  
  // Otherwise, render the legacy component
  return <LegacyComponent />;
}

function App() {
  return (
    <FeatureFlagsProvider>
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
    </FeatureFlagsProvider>
  );
}

/**
 * AppRoutes — Main routing component that conditionally uses Layout or WorkspaceShell
 */
function AppRoutes() {
  const { ready } = useFeatureFlags();
  const isWorkspaceShell = useFeatureFlag('enable_workspace_shell_v1');

  // Show loading while flags load
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500" />
      </div>
    );
  }

  // Use WorkspaceShell for new workspace routes when enabled
  if (isWorkspaceShell) {
    return (
      <Routes>
        {/* Public Routes - still use Layout */}
        <Route element={<Layout />}>
          <Route path="/" element={<ConditionalLanding />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/request-password-reset" element={<RequestPasswordResetPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/deeplink" element={<DeeplinkPage />} />
          <Route path="/phi-guidelines" element={<PhiGuidelinesPage />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/pricing" element={<PricingPage />} />
        </Route>

        {/* Workspace Shell Protected Routes */}
        <Route element={<ProtectedRoute><WorkspaceShell /></ProtectedRoute>}>
          {/* New primary routes */}
          <Route path="/home" element={<WorkspaceHomeRouter />} />
          <Route path="/search" element={<SearchWorkspacePage />} />
          <Route path="/screen" element={<ScreenWorkspacePage />} />
          <Route path="/library" element={<FeatureHubPage />} />
          <Route path="/library/shelf" element={<LearnWorkspacePage />} />
          <Route path="/library/archive" element={<LearnWorkspacePage />} />
          <Route path="/learn" element={<Navigate to="/library/shelf" replace />} />
          <Route path="/digests/manage" element={<DigestProfilesWorkspacePage />} />
          {/* Feature pages — unified under WorkspaceShell for consistent nav */}
          <Route path="/audio-digests" element={<AudioDigestsList />} />
          <Route path="/community" element={<ConditionalCommunity />} />
          <Route path="/litforum/:specialtyId" element={<SpecialtyForumPage />} />
          <Route path="/community/new" element={<NewThreadPage />} />
          <Route path="/community/thread/:threadId" element={<ErrorBoundary fallbackTitle="Thread error"><ThreadDetailPage /></ErrorBoundary>} />
          <Route path="/litscholar" element={<ErrorBoundary fallbackTitle="LitScholar error"><LitScholarPage /></ErrorBoundary>} />
          {/* Settings & Inbox — under WorkspaceShell for consistent rendering */}
          <Route path="/settings" element={<NotificationPreferencesPage />} />
          <Route path="/settings/notifications" element={<NotificationPreferencesPage />} />
          <Route path="/inbox" element={<InboxPage />} />

          {/* ── Navigation V2 Routes (flag-gated) ── */}
          {/* LitScreen V2 */}
          <Route path="/screen/queues" element={<NavV2Gate fallback="/screen"><QueueDigestIndexPage app="screen" /></NavV2Gate>} />
          <Route path="/screen/queues/:profileId" element={<NavV2Gate fallback="/screen"><ScreenWorkspacePage /></NavV2Gate>} />
          <Route path="/screen/articles/:pmid" element={<NavV2Gate fallback="/screen"><ScreenWorkspacePage /></NavV2Gate>} />
          {/* LitHub V2 — canonical `/library`; `/learn` kept for bookmarks */}
          <Route path="/library/digests" element={<NavV2Gate fallback="/library/shelf"><QueueDigestIndexPage app="learn" /></NavV2Gate>} />
          <Route path="/library/digests/:profileId" element={<NavV2Gate fallback="/library/shelf"><LearnWorkspacePage /></NavV2Gate>} />
          <Route path="/library/articles/:pmid" element={<NavV2Gate fallback="/library/shelf"><LearnWorkspacePage /></NavV2Gate>} />
          <Route path="/learn/digests" element={<NavV2Gate fallback="/library/shelf"><QueueDigestIndexPage app="learn" /></NavV2Gate>} />
          <Route path="/learn/digests/:profileId" element={<NavV2Gate fallback="/library/shelf"><LearnWorkspacePage /></NavV2Gate>} />
          <Route path="/learn/articles/:pmid" element={<NavV2Gate fallback="/library/shelf"><LearnWorkspacePage /></NavV2Gate>} />
          {/* LitStream V2 */}
          <Route path="/audio-digests/digests" element={<NavV2Gate fallback="/audio-digests"><QueueDigestIndexPage app="audio-digests" /></NavV2Gate>} />
          <Route path="/audio-digests/digests/:profileId" element={<NavV2Gate fallback="/audio-digests"><AudioDigestsList /></NavV2Gate>} />
          <Route path="/audio-digests/articles/:pmid" element={<NavV2Gate fallback="/audio-digests"><AudioDigestsList /></NavV2Gate>} />
          {/* LitScholar V2 */}
          <Route path="/litscholar/digests" element={<NavV2Gate fallback="/litscholar"><QueueDigestIndexPage app="litscholar" /></NavV2Gate>} />
          <Route path="/litscholar/digests/:profileId" element={<NavV2Gate fallback="/litscholar"><LitScholarPage /></NavV2Gate>} />
          <Route path="/litscholar/articles/:pmid" element={<NavV2Gate fallback="/litscholar"><LitScholarPage /></NavV2Gate>} />
          {/* LitClub V2 */}
          <Route path="/community/digests" element={<NavV2Gate fallback="/community"><QueueDigestIndexPage app="community" /></NavV2Gate>} />
          <Route path="/community/digests/:profileId" element={<NavV2Gate fallback="/community"><ConditionalCommunity /></NavV2Gate>} />
          <Route path="/community/articles/:pmid" element={<NavV2Gate fallback="/community"><ConditionalCommunity /></NavV2Gate>} />
          {/* LitPortal — root always shows embedded app; V2 subroutes gate to /litportal when V2 off */}
          <Route path="/litportal" element={<LitPortalPage />} />
          <Route path="/litportal/digests" element={<NavV2Gate fallback="/litportal"><QueueDigestIndexPage app="litportal" /></NavV2Gate>} />
          <Route path="/litportal/digests/:profileId" element={<NavV2Gate fallback="/litportal"><LitPortalPage /></NavV2Gate>} />
          <Route path="/litportal/articles/:pmid" element={<NavV2Gate fallback="/litportal"><LitPortalPage /></NavV2Gate>} />
          {/* LitBridge legacy alias */}
          <Route path="/litbridge" element={<Navigate to="/litportal" replace />} />
          <Route path="/litbridge/*" element={<Navigate to="/litportal" replace />} />
        </Route>

        {/* Legacy redirects */}
        <Route path="/explore" element={<ProtectedRoute><Navigate to="/search" replace /></ProtectedRoute>} />
        <Route path="/digests" element={<ProtectedRoute><Navigate to="/screen" replace /></ProtectedRoute>} />

        {/* Secondary utility routes - still use Layout */}
        <Route element={<Layout />}>
          <Route path="/verify-email-required" element={<ProtectedRoute><VerifyEmailRequiredPage /></ProtectedRoute>} />
          <Route path="/onboarding/preferences" element={<ProtectedRoute><OnboardingPreferencesPage /></ProtectedRoute>} />
          <Route path="/preferences" element={<ProtectedRoute><ConditionalPreferences /></ProtectedRoute>} />
          <Route path="/library/topics" element={<ProtectedRoute><TopicsDashboardPage /></ProtectedRoute>} />
          <Route path="/article/:id" element={<ProtectedRoute><ErrorBoundary fallbackTitle="Article error"><ArticleDetailPage /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/digests/:digestId" element={<ProtectedRoute><DigestsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><ErrorBoundary fallbackTitle="Admin error"><AdminPage /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/admin/copilot-dashboard" element={<ProtectedRoute><ErrorBoundary fallbackTitle="Dashboard error"><CopilotDashboardPage /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/beta-admin" element={<ProtectedRoute><ErrorBoundary fallbackTitle="Admin error"><BetaAdminPage /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/verification" element={<ProtectedRoute><VerificationPage /></ProtectedRoute>} />
          <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
          <Route path="/plan" element={<ProtectedRoute><ErrorBoundary fallbackTitle="Plan error"><PlanPage /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
          <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
          <Route path="/audio-digests/:audioDigestId" element={<ProtectedRoute><AudioDigestDetail /></ProtectedRoute>} />
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/library" replace />} />
      </Routes>
    );
  }

  // Legacy Layout-based routing (feature flag OFF)
  return (
        <Layout>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<ConditionalLanding />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/request-password-reset" element={<RequestPasswordResetPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Phase A v2: Deep link handler (public, handles auth redirect) */}
            <Route path="/deeplink" element={<DeeplinkPage />} />
          
          {/* Phase SEC-A: Email verification required gate */}
          <Route
            path="/verify-email-required"
            element={
              <ProtectedRoute>
                <VerifyEmailRequiredPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <ConditionalHome />
              </ProtectedRoute>
            }
          />
          {/* Phase UX-E: Onboarding wizard route */}
          <Route
            path="/onboarding/preferences"
            element={
              <ProtectedRoute>
                <OnboardingPreferencesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/preferences"
            element={
              <ProtectedRoute>
                <ConditionalPreferences />
              </ProtectedRoute>
            }
          />
          <Route
            path="/explore"
            element={
              <ProtectedRoute>
                <ConditionalExplore />
              </ProtectedRoute>
            }
          />
          <Route
            path="/screen"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="Screen error">
                  <ScreenWorkspacePage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/screen/queues"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/screen">
                  <QueueDigestIndexPage app="screen" />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/screen/queues/:profileId"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/screen">
                  <ScreenWorkspacePage />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/screen/articles/:pmid"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/screen">
                  <ScreenWorkspacePage />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/library"
            element={
              <ProtectedRoute>
                <FeatureHubPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/shelf"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="Library error">
                  <LearnWorkspacePage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/archive"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="Library archive error">
                  <LearnWorkspacePage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn"
            element={<ProtectedRoute><Navigate to="/library/shelf" replace /></ProtectedRoute>}
          />
          <Route
            path="/library/digests"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/library/shelf">
                  <QueueDigestIndexPage app="learn" />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/digests/:profileId"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/library/shelf">
                  <LearnWorkspacePage />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/articles/:pmid"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/library/shelf">
                  <LearnWorkspacePage />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/digests"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/library/shelf">
                  <QueueDigestIndexPage app="learn" />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/digests/:profileId"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/library/shelf">
                  <LearnWorkspacePage />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/articles/:pmid"
            element={
              <ProtectedRoute>
                <NavV2Gate fallback="/library/shelf">
                  <LearnWorkspacePage />
                </NavV2Gate>
              </ProtectedRoute>
            }
          />
          {/* Phase A v2: Topics Dashboard */}
          <Route
            path="/library/topics"
            element={
              <ProtectedRoute>
                <TopicsDashboardPage />
              </ProtectedRoute>
            }
          />
          {/* Phase A v2: Article Detail */}
          <Route
            path="/article/:id"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="Article error">
                  <ArticleDetailPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/digests"
            element={
              <ProtectedRoute>
                <DigestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/digests/:digestId"
            element={
              <ProtectedRoute>
                <DigestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="Admin error">
                  <AdminPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Beta Admin Dashboard */}
          <Route
            path="/beta-admin"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="Admin error">
                  <BetaAdminPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Phase B: Community Routes */}
          <Route
            path="/community"
            element={
              <ProtectedRoute>
                <ConditionalCommunity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/new"
            element={
              <ProtectedRoute>
                <NewThreadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/thread/:threadId"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="Thread error">
                  <ThreadDetailPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* v2.1: Verification */}
          <Route
            path="/verification"
            element={
              <ProtectedRoute>
                <VerificationPage />
              </ProtectedRoute>
            }
          />
          {/* v2.1: Inbox */}
          <Route
            path="/inbox"
            element={
              <ProtectedRoute>
                <InboxPage />
              </ProtectedRoute>
            }
          />
          {/* v3.0: PHI Guidelines (public route, accessible to all) */}
          <Route path="/phi-guidelines" element={<PhiGuidelinesPage />} />
          {/* Membership comparison (public) */}
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* v3.0 Step 4: Plan page */}
          <Route
            path="/plan"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="Plan error">
                  <PlanPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Phase 7: Audio Digests V2 */}
          <Route path="/settings" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
          <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
          <Route
            path="/audio-digests"
            element={<ProtectedRoute><AudioDigestsList /></ProtectedRoute>}
          />
          <Route
            path="/audio-digests/:audioDigestId"
            element={<ProtectedRoute><AudioDigestDetail /></ProtectedRoute>}
          />
          {/* LitScholar Deep Dive Page */}
          <Route
            path="/litscholar"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackTitle="LitScholar error">
                  <LitScholarPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* LitPortal — federated search iframe (same as workspace shell) */}
          <Route path="/litportal" element={<ProtectedRoute><LitPortalPage /></ProtectedRoute>} />
          <Route path="/litportal/digests" element={<ProtectedRoute><NavV2Gate fallback="/litportal"><QueueDigestIndexPage app="litportal" /></NavV2Gate></ProtectedRoute>} />
          <Route path="/litportal/digests/:profileId" element={<ProtectedRoute><NavV2Gate fallback="/litportal"><LitPortalPage /></NavV2Gate></ProtectedRoute>} />
          <Route path="/litportal/articles/:pmid" element={<ProtectedRoute><NavV2Gate fallback="/litportal"><LitPortalPage /></NavV2Gate></ProtectedRoute>} />
          <Route path="/litbridge" element={<Navigate to="/litportal" replace />} />
          <Route path="/litbridge/*" element={<Navigate to="/litportal" replace />} />

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
  );
}

export default App;
